import { useMemo } from 'react'
import { cn } from '@ludus/ui'
import type { InternalStatus, StatusId } from '@ludus/core'
import { getStageColor } from '@/shared/utils/stageColors.ts'

interface StageProgressBarProps {
  statuses: InternalStatus[]
  currentStatusId: StatusId
}

interface ComputedStage {
  id: StatusId
  index: number
  label: string
  state: 'done' | 'current' | 'pending'
  /** CSS color (active palette value) for this stage's dot + segments. */
  color: string
}

/**
 * Dots-on-line progress indicator (reference: tmp/Funnels...D-1067.png).
 *
 * Layout: each stage is a flex cell that owns a half-segment of connector
 * line to its left and right neighbours, plus a centered dot. Segments
 * stop AT the dot — they do not run under it — which is what makes the
 * "broken line, dots set into it" appearance work. Per-segment color is
 * picked from the stage palette (`stageColors.ts`) so the strip carries
 * cool-to-warm progression instead of a single brand line.
 *
 * State semantics:
 * - done segments + dots: filled in their stage palette colour.
 * - current dot: primary fill + ring/glow for prominence.
 * - pending segments + dots: muted border-only.
 */
export function StageProgressBar({ statuses, currentStatusId }: StageProgressBarProps) {
  const { stages, currentIndex } = useMemo(() => {
    const ordered = [...statuses].sort((a, b) => a.position - b.position)
    const idx = ordered.findIndex((s) => s.id === currentStatusId)
    const computed: ComputedStage[] = ordered.map((s, i) => ({
      id: s.id,
      index: i,
      label: s.name,
      state: idx < 0 ? 'pending' : i < idx ? 'done' : i === idx ? 'current' : 'pending',
      color: getStageColor(i, ordered.length).active,
    }))
    return { stages: computed, currentIndex: idx < 0 ? -1 : idx }
  }, [statuses, currentStatusId])

  if (stages.length === 0) return null

  return (
    // On narrow screens the markers keep a legible min width and the row scrolls
    // horizontally instead of truncating every label to a few characters; on
    // desktop everything fits, so no scroll affordance shows.
    <div className="overflow-x-auto sm:overflow-x-visible">
      <div role="list" aria-label="Pipeline stages" className="flex w-full items-start">
        {stages.map((s, i) => (
          <StageMarker
            key={s.id}
            stage={s}
            isFirst={i === 0}
            isLast={i === stages.length - 1}
            leftSegmentDone={
              // segment (i-1 → i) is filled when stage[i] is done or current
              currentIndex >= 0 && i <= currentIndex
            }
            rightSegmentDone={
              // segment (i → i+1) is filled when stage[i+1] is done or current
              currentIndex >= 0 && i < currentIndex
            }
            leftSegmentColor={i > 0 ? stages[i - 1].color : s.color}
            rightSegmentColor={s.color}
          />
        ))}
      </div>
    </div>
  )
}

interface StageMarkerProps {
  stage: ComputedStage
  isFirst: boolean
  isLast: boolean
  leftSegmentDone: boolean
  rightSegmentDone: boolean
  leftSegmentColor: string
  rightSegmentColor: string
}

function StageMarker({
  stage,
  isFirst,
  isLast,
  leftSegmentDone,
  rightSegmentDone,
  leftSegmentColor,
  rightSegmentColor,
}: StageMarkerProps) {
  const number = String(stage.index + 1).padStart(2, '0')
  return (
    <div
      role="listitem"
      aria-current={stage.state === 'current' ? 'step' : undefined}
      title={stage.label}
      className="flex min-w-[72px] flex-1 flex-col items-center gap-2.5 sm:min-w-0"
    >
      {/* Dot row: left half-segment + dot + right half-segment.
          The segments stop at the dot's edge (no overlap), so the line
          reads as broken-by-dot rather than dots-on-line. */}
      <div className="flex h-4 w-full items-center">
        <Segment hidden={isFirst} done={leftSegmentDone} color={leftSegmentColor} />
        <Dot state={stage.state} color={stage.color} />
        <Segment hidden={isLast} done={rightSegmentDone} color={rightSegmentColor} />
      </div>

      <div className="flex w-full min-w-0 flex-col items-center gap-0.5 text-center">
        <span
          className={cn(
            'font-mono text-[10px] font-semibold tracking-[0.18em]',
            stage.state === 'current'
              ? 'text-primary'
              : stage.state === 'done'
                ? 'text-foreground/60'
                : 'text-muted/55',
          )}
        >
          {number}
        </span>
        <span
          className={cn(
            'w-full truncate text-[11px] font-medium tracking-wide',
            stage.state === 'current'
              ? 'text-foreground'
              : stage.state === 'done'
                ? 'text-foreground/80'
                : 'text-muted/75',
          )}
        >
          {stage.label}
        </span>
      </div>
    </div>
  )
}

function Segment({ hidden, done, color }: { hidden: boolean; done: boolean; color: string }) {
  return (
    <span
      aria-hidden="true"
      className="h-[2px] flex-1 rounded-full"
      style={
        hidden
          ? { background: 'transparent' }
          : done
            ? { background: color, opacity: 0.75 }
            : { background: 'var(--color-border)', opacity: 0.35 }
      }
    />
  )
}

function Dot({ state, color }: { state: ComputedStage['state']; color: string }) {
  // 14px dots so labels under them have a meaningful anchor. Current dot
  // uses primary + box-shadow ring/glow so it reads ~18-20px visually
  // without breaking the segment alignment (footprint stays 14px).
  if (state === 'current') {
    return (
      <span
        aria-hidden="true"
        className="mx-1 inline-block h-3.5 w-3.5 shrink-0 rounded-full bg-primary shadow-[0_0_0_3px_rgba(99,102,241,0.28),0_0_16px_rgba(99,102,241,0.55)]"
      />
    )
  }
  if (state === 'done') {
    return (
      <span
        aria-hidden="true"
        className="mx-1 inline-block h-3.5 w-3.5 shrink-0 rounded-full"
        style={{ background: color, opacity: 0.85 }}
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      className="mx-1 inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-border/60 bg-background-secondary"
    />
  )
}
